import { getIdToken } from "../auth/AuthProvider.js";

/**
 * Tiny fetch wrapper that attaches the current Cognito ID token as a
 * Bearer Authorization header. The API base URL is injected at build time
 * via `VITE_API_BASE_URL` (set up in unit 7).
 *
 * Returns parsed JSON for 2xx, throws an Error otherwise.
 */
export interface ApiOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL is not set in this build.");
  }

  const token = await getIdToken();
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const init: RequestInit = { ...options, headers };
  if (options.json !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(options.json);
  }

  const url = baseUrl.replace(/\/$/, "") + (path.startsWith("/") ? path : `/${path}`);
  const res = await fetch(url, init);
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(`API ${res.status} ${res.statusText}`, res.status, parsed);
  }
  return parsed as T;
}
